<template lang="pug">
#v-terminal(:class="{ visible: isOpen }")
  #window.px-2
    .bg-dark.rounded.p-3.shadow.overflow-auto(
      ref="scroller"
    )
      a#close.position-absolute.text-success(href="#", @click.prevent="toggle()")
        font-awesome-icon(:icon="['far','times']")
      .font-monospace.text-terminal.mb-0(
          v-for="(item, idx) in messages",
          :key="idx"
        )
        span.datetime {{ item.datetime }}
        | {{ ` ${item.text}` }}
        span.cursor(v-if="idx === messages.length - 1")

  button#v-terminal-button.btn.btn-dark.pb-2(@click="toggle()")
    span.position-absolute.top-0.start-100.translate-middle.badge.border.border-light.rounded-circle.bg-danger.p-2(
      :is-read="isRead"
    )
      span.visually-hidden unread messages
    font-awesome-icon.me-1(:icon="['far','terminal']")
</template>

<script>
const MS_PER_LETTER = 70
const MS_BETWEEN_MESAGES = 300

export default {
  props: {
    lastMessages: { type: Array, required: true }
  },
  data: () => ({
    isOpen: false,
    isRead: true,
    isTyping: false,
    isTypingWord: false,
    messages: [],
    messagesQueue: []
  }),
  watch: {
    lastMessages: {
      handler(newMessages) {
        if (!this.isOpen) {
          this.isRead = false
        }
        this.scrollToBottom()
        const from = this.messagesQueue.length + this.messages.length
        this.messagesQueue.push(...newMessages.slice(from))
      },
      deep: true
    },
    messagesQueue: {
      handler(queue) {
        if (!queue.length || this.isTyping || !this.isOpen) return
        this.isTyping = true
        this.typeMessagesByOrder()
      },
      deep: true
    },
    isOpen(val) {
      if (val && this.messagesQueue.length && !this.isTyping) {
        this.isTyping = true
        this.typeMessagesByOrder()
      }
    }
  },
  created() {
    this.messages = [...this.lastMessages]
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen
      if (this.isOpen) this.isRead = true
      this.scrollToBottom()
    },
    scrollToBottom() {
      this.$nextTick(() => {
        let scroller = this.$refs.scroller
        scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight
      })
    },
    async typeMessagesByOrder() {
      if (!this.messagesQueue.length) {
        this.isTyping = false
        return
      }
      const { datetime, text } = this.messagesQueue.shift()
      this.messages.push({ datetime, text: '' })
      this.scrollToBottom()
      await this.typeLastMessageByLetters(text)
      setTimeout(this.typeMessagesByOrder, MS_BETWEEN_MESAGES)
    },
    typeLastMessageByLetters(fullMessage, letterIndex = 0) {
      return new Promise((resolve) => {
        this.messages[this.messages.length - 1].text += fullMessage[letterIndex]
        letterIndex++
        if (fullMessage.length > letterIndex) {
          setTimeout(() => {
            resolve(this.typeLastMessageByLetters(fullMessage, letterIndex))
          }, MS_PER_LETTER)
        } else {
          resolve()
        }
      })
    }
  }
}
</script>

<style lang="sass" scoped>
@function shade-color($color, $weight)
  @return mix(black, $color, $weight)

#v-terminal
  #v-terminal-button
    position: fixed
    bottom: 0
    transition: transform 0.125s ease-out
    z-index: 1100
    transform: translate(0, .25rem)
    right: .5rem

  #window
    position: fixed
    bottom: 0rem
    right: 0rem
    z-index: 1100
    transition: transform 0.125s ease-out
    transform: translate(0, 100%)
    max-width: 32rem
    @media (min-width: 576px)
      width: 480px
    width: 100%
    > div
      height: 12rem
    #close
      right: 1.5rem
      top: 0.5rem
  &.visible
    #window
      transform: translate(0, -.5rem)
    #v-terminal-button
      transform: translate(0, 105%)

  span
    transition: opacity 0.25s
    &[is-read="true"]
      opacity: 0

$cursor-color: lime

.text-terminal
  color: $cursor-color
  .datetime
    color: shade-color($cursor-color, 40%)
    margin-right: 0.5rem

  .cursor
    color: $cursor-color
    line-height: 1rem
    font-size: 1.2rem
    animation: 0.5s linear infinite alternate blink-cursor
    &:after
      content: "▮"


@keyframes blink-cursor
  to
    color: #343a40

[v-cloak]
  display: none
</style>
